import { Badge, Box, Button, DrawerBody, DrawerFooter, FormControl, FormHelperText, FormLabel, Input, Stack, useToast } from "@chakra-ui/react";
import { DrawerContext } from "components/drawer";
import SearchSelect from "components/select/SearchSelect";
import { AuthContext } from "contexts/AuthContext";
import { DataContext } from "contexts/DataContext";
import { useLocation } from "hooks/useLocation";
import { selectableLocations } from "lib/devices";
import { createDeviceRecord, findDeviceByLocation, getAll, saveDeviceRecord } from "lib/firebase";
import { useContext, useEffect, useRef, useState } from "react";

export default function DeviceModal({ device, onChange }) {
  const [data, setData] = useState(device || {})
  const { user } = useContext(AuthContext)

  // While saving, the buttons are disabled. `saving` is set at once (a state change shows only
  // after the next redraw), so a second click right after the first is ignored: that is how
  // the same device used to be created twice.
  const [isSaving, setIsSaving] = useState(false)
  const saving = useRef(false)

  const { closeDrawer } = useContext(DrawerContext)

  const toast = useToast()

  const [locations] = useLocation({user})

  // All devices of the client, to tell which locations are taken. It starts from the list
  // the app already holds and is replaced by fresh data as soon as that is loaded.
  const { devices: cachedDevices } = useContext(DataContext)
  const [allDevices, setAllDevices] = useState(cachedDevices[0])

  useEffect(() => {
    let active = true
    if (user?.clientId) {
      getAll('device', user.clientId)
        .then(fresh => { if (active) setAllDevices(fresh) })
        .catch(() => { /* the cached list stays; the check before saving uses fresh data anyway */ })
    }
    return () => { active = false }
  }, [user?.clientId])

  // Only locations without a device; when editing, also the one this device already has
  const freeLocations = selectableLocations(locations, allDevices, device)

  // Only another device replaces the form, not the same device arriving again as a new object
  // (the list is reloaded by Refresh): that must not wipe what is being typed
  useEffect(() => {
    if (device) {
      setData(device || {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id])

  // A location can have only one device. The list above is a convenience; this is the real
  // check, against fresh data, so two people editing at the same time cannot both take it.
  const ensureLocationFree = async (clientId, deviceId) => {
    const taken = await findDeviceByLocation(clientId, data.locationId, deviceId)
    if (taken) {
      throw new Error(`This location already has a device (${taken.code || taken.id}).`)
    }
  }

  const updateDevice = async () => {
    if (saving.current) return
    saving.current = true
    setIsSaving(true)
    delete data.location
    try {
      const clientId = data.clientId || user.clientId
      // A device that keeps the location it already has is not blocked by an older record
      // that shares it; only a CHANGE of location has to be free
      if (data.locationId !== device.locationId) {
        await ensureLocationFree(clientId, device.id)
      }
      // The code has to be unique within the client (checked here, against fresh data)
      await saveDeviceRecord(clientId, device.id, data, device.code)
      onChange && onChange()
      closeDrawer()
      toast({ title: 'Device updated', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message, status: 'error', duration: null, isClosable: true })
    } finally {
      saving.current = false
      setIsSaving(false)
    }
  }

  const createDevice = async () => {
    if (saving.current) return
    saving.current = true
    setIsSaving(true)
    delete data.location
    try {
      const clientId = user.clientId
      await ensureLocationFree(clientId)
      // The code is required and has to be unique within the client
      await createDeviceRecord({ ...data, clientId })
      onChange && onChange()
      closeDrawer()
      toast({ title: 'Device created', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message, status: 'error', duration: null, isClosable: true })
    } finally {
      saving.current = false
      setIsSaving(false)
    }
  }

  const color = {
    'OK': 'green',
    'ERROR': 'red'
  }[data.status]
  
  return (
    <>
      <DrawerBody>
        <Stack direction='column' spacing={4}>
          <FormControl>
            <FormLabel>ID</FormLabel>
            <Input type='text' value={data.id || 'ID will be auto-generated'} disabled />
          </FormControl>
          <FormControl>
            <FormLabel>Device code</FormLabel>
            <Input type='text' value={data.code} onChange={e => setData({...data, code: e.target.value })} />
            <FormHelperText>Device code used for setup on Android device. Required and unique: spaces around it and upper / lower case are ignored.</FormHelperText>
          </FormControl>
          <FormControl>
            <FormLabel>Location</FormLabel>
            <SearchSelect placeholder='Select location'
              options={freeLocations.map(loc => ({ value: loc.id, label: loc.name, hint: loc.city || undefined }))}
              value={data.locationId}
              onChange={locationId => setData({...data, locationId})} />
            <FormHelperText>
              {freeLocations.length === 0
                ? 'Every location already has a device.'
                : 'Only locations without a device are listed.'}
            </FormHelperText>
          </FormControl>
          {data.id && (
            <>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Badge fontSize='0.7em' colorScheme={color}>{data.status || 'N/A'}</Badge>
              </FormControl>
              <FormControl>
                <FormLabel>Message</FormLabel>
                <div>{data.message}</div>
              </FormControl>
              <hr />
              <FormControl>
                <FormLabel>Android version</FormLabel>
                <div>{data.androidVersion}</div>
              </FormControl>
              <FormControl>
                <FormLabel>Device model</FormLabel>
                <div>{data.model}</div>
              </FormControl>
              <FormControl>
                <FormLabel>Orientation</FormLabel>
                <div>{data.orientation}</div>
              </FormControl>
            </>
          )}
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        {device && (
          <Button colorScheme='brand' borderRadius="30px" me="10px" isLoading={isSaving} onClick={() => updateDevice()}>
            Save
          </Button>
        )}
        {!device && (
          <Button colorScheme='brand' borderRadius="30px" me="10px" isLoading={isSaving} onClick={() => createDevice()}>
            Create
          </Button>
        )}
      </DrawerFooter>
    </>
  );
}